import BillingService from "../services/api/billing";
import AgentService from "../services/api/agents";
import WorkflowService from "../services/api/workflow";
import AnalyticsService from "../services/api/analytics";
import { API_RESPONSE } from "../settings/shared/constants";

export const RANGE_PRESETS = {
  "7d": 7,
  "14d": 14,
  "30d": 30,
};

export const toISODate = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

export const getRangeForPreset = (preset) => {
  const days = RANGE_PRESETS[preset] || 7;
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return { startDate: toISODate(start), endDate: toISODate(end) };
};

/** API payload: preset range or custom with ISO dates; optional projectId / userId for scoped views */
export const buildAnalyticsPayload = ({
  rangePreset = "7d",
  startDate,
  endDate,
  projectId,
  userId,
}) => {
  const base =
    rangePreset === "custom"
      ? { range: "custom", startDate, endDate }
      : { range: rangePreset || "7d" };

  if (projectId) {
    return { ...base, projectId };
  }
  if (userId) {
    return { ...base, userId };
  }
  return base;
};

const makeDayKey = (d) => toISODate(d);

const formatDayLabel = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** Parse API date strings such as "18-05-2026" (DD-MM-YYYY) */
export const parseApiAnalyticsDate = (raw) => {
  if (!raw) return null;
  const str = String(raw).trim();
  const dmy = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmy) {
    const [, dd, mm, yyyy] = dmy;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const buildDailySeriesSkeleton = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  const out = [];
  for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = makeDayKey(d);
    out.push({
      key,
      label: formatDayLabel(new Date(d)),
      consumed: 0,
      traces: 0,
      sessions: 0,
      executionTime: 0,
      tokens: 0,
    });
  }
  return out;
};

const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const isSuccessStatus = (raw) => {
  const s = String(raw || "").toUpperCase();
  return s === "SUCCESS" || s === "COMPLETED" || s === "FINISHED" || s === "DONE";
};

const isFailedStatus = (raw) => {
  const s = String(raw || "").toUpperCase();
  return s === "FAILED" || s === "ERROR" || s === "FAILURE";
};

const parseTraceDate = (trace) => {
  const raw = trace?.createdDate || trace?.updatedDate || trace?.timestamp;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const extractHistoryList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response?.workFlowTemplateDTOPage?.content)) {
    return response.workFlowTemplateDTOPage.content;
  }
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const agentNamesFromTrace = (trace) => {
  const nodes = Array.isArray(trace?.nodes) ? trace.nodes : [];
  return nodes
    .filter((n) => n?.type === "agent" || n?.type === "task")
    .map((n) => n?.data?.title || n?.title || n?.data?.label || n?.id)
    .filter(Boolean);
};

const hasAnalyticsError = (body) => {
  const status = body?.status;
  if (status === API_RESPONSE.FAILED || status === API_RESPONSE.FAILURE) return true;
  return Boolean(body?.errorCode || body?.errorMessage);
};

/** Normalize observability analytics API response */
export const normalizeAnalyticsResponse = (body, startDate, endDate) => {
  const rows = Array.isArray(body?.analytics) ? body.analytics : [];
  const dailyMap = new Map();

  let successTotal = 0;
  let failedTotal = 0;
  let rangeTotalTokens = 0;
  let rangeExecutions = 0;
  let rangeExecutionTime = 0;

  rows.forEach((row) => {
    const parsed = parseApiAnalyticsDate(row?.date);
    const key = parsed ? toISODate(parsed) : String(row?.date || "");
    const tokens = asNum(row?.totalTokens);
    const executions = asNum(row?.executionCount);
    const success = asNum(row?.successCount);
    const failed = asNum(row?.failedCount);

    successTotal += success;
    failedTotal += failed;
    rangeTotalTokens += tokens;
    rangeExecutions += executions;

    if (!key) return;

    const executionTime = asNum(
      row?.totalExecutionTimeInSeconds ?? row?.totalExecutionTime ?? row?.executionTime,
    );
    rangeExecutionTime += executionTime;

    dailyMap.set(key, {
      key,
      label: parsed ? formatDayLabel(parsed) : String(row?.date || key),
      consumed: tokens,
      traces: executions,
      sessions: executions,
      tokens,
      executionTime,
    });
  });

  const skeleton = buildDailySeriesSkeleton(startDate, endDate);
  const dailySeries = (skeleton.length ? skeleton : [...dailyMap.values()])
    .map((day) => {
      const fromApi = dailyMap.get(day.key);
      if (!fromApi) return day;
      return { ...day, ...fromApi };
    })
    .sort((a, b) => String(a.key).localeCompare(String(b.key)));

  return {
    source: "api",
    summary: {
      totalTokens: rangeTotalTokens,
      totalConsumed: rangeTotalTokens,
      totalTraces: rangeExecutions,
      totalSessions: rangeExecutions,
      totalExecutionTime: rangeExecutionTime,
    },
    dailySeries,
    successFailed: {
      success: successTotal,
      failed: failedTotal,
    },
    topAgents: [],
  };
};

export const normalizeDashboardResponse = (res, startDate, endDate) => {
  const body = res?.data ?? res ?? {};

  if (Array.isArray(body.analytics)) {
    return normalizeAnalyticsResponse(body, startDate, endDate);
  }

  const summary = body.summary || body;
  const daily = body.dailySeries || body.daily || body.series || [];

  const dailySeries =
    Array.isArray(daily) && daily.length > 0
      ? daily.map((row) => ({
          key: row.date || row.key || row.day,
          label: row.label || formatDayLabel(new Date(row.date || row.key)),
          consumed: asNum(row.consumed ?? row.credits ?? row.usage ?? row.totalTokens),
          traces: asNum(row.traces ?? row.executionCount),
          sessions: asNum(row.sessions ?? row.executionCount),
          executionTime: asNum(
            row.executionTime ??
              row.totalExecutionTimeInSeconds ??
              row.totalExecutionTime,
          ),
          tokens: asNum(row.tokens ?? row.totalTokens),
        }))
      : buildDailySeriesSkeleton(startDate, endDate);

  const sf = body.successFailed || body.requestOutcomes || {};
  const topAgents = (body.topAgents || body.topAgentsUsage || []).slice(0, 5);

  return {
    source: "api",
    summary: {
      totalTokens: asNum(summary.totalTokens ?? summary.tokens),
      totalConsumed: asNum(summary.totalConsumed ?? summary.consumed ?? summary.credits),
      totalTraces: asNum(summary.totalTraces ?? summary.traces),
      totalSessions: asNum(summary.totalSessions ?? summary.sessions),
    },
    dailySeries,
    successFailed: {
      success: asNum(sf.success ?? sf.succeeded),
      failed: asNum(sf.failed ?? sf.failure),
    },
    topAgents: topAgents.map((a) => ({
      name: a.name || a.agentName || "Unknown",
      usage: asNum(a.usage ?? a.count ?? a.executions),
      tokens: asNum(a.tokens),
    })),
  };
};

/** Total tokens from observability usage-summary (analytics[0].totalTokens) */
export const parseTokensUsageSummary = (body) => {
  if (hasAnalyticsError(body) || !Array.isArray(body?.analytics)) return null;
  const totalTokens = body.analytics.reduce((sum, row) => sum + asNum(row?.totalTokens), 0);
  return totalTokens;
};

/** Account-wide total tokens (observability usage-summary only). */
export const fetchAccountTokensUsageSummary = async () => {
  try {
    const res = await AnalyticsService.getAccountTokensUsageSummary({});
    const body = res?.data ?? res ?? {};
    const totalTokens = parseTokensUsageSummary(body);
    if (totalTokens == null) return null;
    return { totalTokens };
  } catch {
    return null;
  }
};

export const buildFallbackDashboard = async (startDate, endDate) => {
  const dailySeries = buildDailySeriesSkeleton(startDate, endDate);
  const byDay = Object.fromEntries(dailySeries.map((d) => [d.key, d]));

  let totalTokens = 0;
  let totalConsumed = 0;
  let successCount = 0;
  let failedCount = 0;
  const agentUsage = new Map();

  const projectsRes = await AgentService.getProject(0, {}).catch(() => null);
  const projects =
    projectsRes?.projectDTOS?.content ||
    projectsRes?.content ||
    (Array.isArray(projectsRes) ? projectsRes : []);

  const [summaryRes, usageRes] = await Promise.all([
    BillingService.getAccountCreditUsageSummary().catch(() => null),
    BillingService.getAccountCreditUsage("DAY", { startDate, endDate }).catch(() => null),
  ]);

  if (summaryRes?.status === API_RESPONSE.SUCCESS) {
    const su = summaryRes.summaryUsage || summaryRes || {};
    const cu = summaryRes.creditUsage || {};
    totalConsumed = asNum(su.totalCredits ?? cu?.credits?.used);
    totalTokens = asNum(su.totalTokens ?? totalConsumed);
  }

  if (usageRes?.status === API_RESPONSE.SUCCESS && Array.isArray(usageRes.usages)) {
    usageRes.usages.forEach((row) => {
      if (!row?.date || !byDay[row.date]) return;
      const c = asNum(row.credits);
      byDay[row.date].consumed += c;
    });
  }

  const projectList = Array.isArray(projects) ? projects : [];
  const historyTargets = projectList.slice(0, 12);

  const historyResults = await Promise.all(
    historyTargets.map((p) => {
      const id = p?.id || p?._id;
      if (!id) return Promise.resolve([]);
      return WorkflowService.getWorkflowHistory(id, 0, 80)
        .then(extractHistoryList)
        .catch(() => []);
    }),
  );

  const rangeStart = new Date(startDate);
  const rangeEnd = new Date(endDate);
  rangeEnd.setHours(23, 59, 59, 999);

  const sessionIds = new Set();
  let totalTraces = 0;

  historyResults.flat().forEach((trace) => {
    const d = parseTraceDate(trace);
    if (!d || d < rangeStart || d > rangeEnd) return;

    totalTraces += 1;
    const key = makeDayKey(d);
    if (byDay[key]) {
      byDay[key].traces += 1;
      byDay[key].sessions += 1;
    }

    const execId = trace?.executionId || trace?.id || trace?.workflowExecutionId;
    if (execId) sessionIds.add(String(execId));

    const tokens = asNum(trace?.totalTokens);
    totalTokens += tokens;
    if (byDay[key]) byDay[key].tokens += tokens;

    const wf = trace?.wfStatus ?? trace?.status;
    if (isSuccessStatus(wf)) successCount += 1;
    else if (isFailedStatus(wf)) failedCount += 1;

    agentNamesFromTrace(trace).forEach((name) => {
      const prev = agentUsage.get(name) || { name, usage: 0, tokens: 0 };
      prev.usage += 1;
      prev.tokens += tokens;
      agentUsage.set(name, prev);
    });
  });

  const totalSessions = sessionIds.size || totalTraces;
  dailySeries.forEach((row) => {
    if (!row.sessions && row.traces) row.sessions = row.traces;
  });

  const topAgents = [...agentUsage.values()]
    .sort((a, b) => b.usage - a.usage || b.tokens - a.tokens)
    .slice(0, 5);

  return {
    source: "fallback",
    summary: {
      totalTokens,
      totalConsumed,
      totalTraces,
      totalSessions,
    },
    dailySeries,
    successFailed: { success: successCount, failed: failedCount },
    topAgents,
  };
};

export const fetchDashboardData = async ({
  startDate,
  endDate,
  rangePreset = "7d",
  projectId,
  userId,
}) => {
  try {
    const payload = buildAnalyticsPayload({
      rangePreset,
      startDate,
      endDate,
      projectId,
      userId,
    });
    const res = await AnalyticsService.getAccountDashboard({ payload });
    const body = res?.data ?? res ?? {};

    if (!hasAnalyticsError(body) && Array.isArray(body.analytics)) {
      return normalizeAnalyticsResponse(body, startDate, endDate);
    }

    if (res?.status === API_RESPONSE.SUCCESS) {
      return normalizeDashboardResponse(res, startDate, endDate);
    }
  } catch (e) {
    console.warn("[Dashboard] analytics API failed, using fallback", e);
  }

  return buildFallbackDashboard(startDate, endDate);
};

export const formatMetric = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

export const formatTokenCount = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "0";
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

/** Format seconds as running time (e.g. 905 → "15m 5s", 0.9 → "900ms"). */
export const formatExecutionDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return "0s";
  if (s < 1) {
    const ms = Math.round(s * 1000);
    return `${ms.toLocaleString("en-US")}ms`;
  }
  if (s < 60) {
    return s < 10 && !Number.isInteger(s)
      ? `${s.toFixed(1)}s`
      : `${Math.round(s).toLocaleString("en-US")}s`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.round(s % 60);
  if (h > 0) {
    return m > 0 || sec > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
};

export const formatCreditAmount = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatBillingDisplayDate = (raw) => {
  if (raw == null || raw === "") return null;
  const d = parseApiAnalyticsDate(raw) ?? new Date(raw);
  if (!d || Number.isNaN(d.getTime())) return null;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-GB", { month: "short" });
  return `${day} ${month} ${d.getFullYear()}`;
};

/** Account credit balance for dashboard header (remaining + next billing). */
export const extractCreditSummary = (billingRes) => {
  if (!billingRes || billingRes.status !== API_RESPONSE.SUCCESS) return null;

  const cu = billingRes.creditUsage || {};
  const credits = cu.credits || {};
  const total = asNum(credits.total);
  const used = asNum(credits.used);
  let remaining =
    credits.remaining != null && credits.remaining !== ""
      ? asNum(credits.remaining)
      : Math.max(total - used, 0);

  const rawDate =
    cu.nextBillingDate ??
    cu.nextBilling ??
    cu.billingDate ??
    cu.cycleEndDate ??
    cu.subscriptionEndDate;

  let nextBilling = formatBillingDisplayDate(rawDate);
  if (!nextBilling && cu.resetIn != null) {
    const days = asNum(cu.resetIn);
    if (days > 0) {
      const d = new Date();
      d.setDate(d.getDate() + Math.ceil(days));
      nextBilling = formatBillingDisplayDate(d);
    }
  }

  return { creditsRemaining: remaining, nextBillingDate: nextBilling };
};

export const fetchCreditSummary = async () => {
  try {
    const res = await BillingService.getAccountCreditUsageSummary();
    return extractCreditSummary(res);
  } catch {
    return null;
  }
};
